import React, { useState } from 'react';
import * as Sentry from '@sentry/browser';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Cart from './Cart';
import countries from './countries';
import checkoutAnalytics from './checkoutAnalytics';
import Button from '../Elements/Button/Button';
import Input from '../Elements/Input/Input';
import Select from '../Elements/Select/Select';
import Checkbox from '../Elements/Checkbox/Checkbox';
import Breadcrumbs from '../Category/Breadcrumbs';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import { changeCountry } from '../../actions/environment';
import { updateCheckoutAddress, changeAddressInput, checkAddressInputErrors } from '../../actions/checkout';
import { purifier } from '../../utils/dom';
import { translate, prefix } from '../../utils/locale';
import { upperCaseFirst, fieldValidation } from '../../utils/string';
import { addressFieldOrder } from '../../config';
import s from './Selection.scss';

const Selection = ({ checkout, setup, ...props }) => {
	const countryKey = Object.keys(countries).filter((c) => countries[c].code === setup.country);
	const countryDetails = countries[countryKey];
	const hasProvinces = Object.keys(countryDetails.province_codes).length > 0;
	const hasItems = checkout.selection.items && checkout.selection.items.length > 0;

	const [formErrors, toggleErrors] = useState(addressFieldOrder.reduce((acc, f) => ({
		...acc,
		[f.id]: false,
	}), {}));
	const [buttonError, toggleButtonError] = useState(false);
	const [errorMessage, updateErrorMessage] = useState('');

	const termsCheckedHandler = (id, checked) => {
		props.changeAddressInput('address', id, !!checked);
		toggleButtonError(false);
		updateErrorMessage('');
		toggleErrors((prevState) => ({
			...prevState,
			[id]: !checked,
		}));
	};

	const checkoutSubmitHandler = (e) => {
		e.preventDefault();
		let hasErrors = false;

		// Check form for errors.
		const errorState = addressFieldOrder.reduce((acc, f) => {
			let isError = f.required && fieldValidation(checkout?.payment?.address?.[f.id], f.id);
			if (f.id === 'province' && !hasProvinces) isError = false;
			if (isError) hasErrors = true;
			return {
				...acc,
				[f.id]: isError,
			};
		}, {});
		toggleErrors(errorState);

		if (hasErrors) {
			toggleButtonError(true);
		} else if (checkout.selection.items && checkout.selection.items.length > 0) {
			checkoutAnalytics(checkout.selection.items, setup, checkout.selection.totals);
			props.updateCheckoutAddress().then((id) => {
				window.location = checkout.selection.webUrl;
				return id;
			}).catch((err) => {
				Sentry.captureMessage('Shopify returned an error when customer was checking out.');
				Sentry.captureException(err);
				if (err.checkoutUserErrors) {
					toggleButtonError(true);
					toggleErrors((prevState) => {
						err.checkoutUserErrors.forEach((checkoutUserError) => {
							prevState[checkoutUserError.id] = true;
						});

						return prevState;
					});
					updateErrorMessage(() => {
						const message = err.checkoutUserErrors.reduce((acc, checkoutUserError) => (
							`${acc}\n${checkoutUserError.message}`
						), '').replace(/^\s+/, '');

						return message;
					});
				}
			});
		}
	};

	const onAddressInputChange = (id, value) => {
		const group = id.slice(0, id.indexOf('-'));
		const fieldId = id.slice(id.indexOf('-') + 1);
		const isRequired = addressFieldOrder.filter((f) => f.id === fieldId)[0]?.required;
		toggleButtonError(false);
		updateErrorMessage('');
		props.changeAddressInput(group, fieldId, value);
		toggleErrors((prevState) => ({
			...prevState,
			[fieldId]: (isRequired || (fieldId === 'province' && hasProvinces)) && fieldValidation(checkout?.payment?.address?.[fieldId], fieldId),
		}));
	};

	const onCountryChange = (id, value) => {
		props.changeCountry(value);
	};

	return (
		<main role="main">
			<div className={s.selectionHeader}>
				<span>{translate(setup, 'checkoutTitle') }</span>
				<Link className={s.selectionHeader_link} to={prefix(setup, 'shop')}>{ translate(setup, 'checkoutBackToShop') }</Link>
			</div>
			<div className={[s.selectionHeader, s.selectionHeader__secondary].join(' ')}>
				<Breadcrumbs />
				<span className={s.selectionHeader_items}>{ translate(setup, 'checkoutItems') }</span>
			</div>
			{
				hasItems > 0 ? (
					<div className={s.selection}>
						<div className={s.selection_info}>
							<form method="post" onSubmit={checkoutSubmitHandler}>
								<div className={s.selection_info_address}>
									<h2 className={s.selection_title}>{ translate(setup, 'checkoutInformationTitle') }</h2>
									{
										addressFieldOrder.map((field) => {
											if (field.id === 'termsAndConditions') return null;
											if (field.id === 'province') {
												if (Object.keys(countryDetails.province_codes).length > 0) {
													const options = [{ key: '', value: translate(setup, 'chooseProvince') }, ...Object.keys(countryDetails.province_codes).map((p) => ({ key: countryDetails.province_codes[p], value: p }))];
													return <Select variantClasses={['select__checkout']} key={field.id} id={`address-${field.id}`} options={options} selected={checkout?.payment?.address?.[field.id]} inputChange={onAddressInputChange} isError={formErrors[field.id]} />;
												}
												return null;
											}
											if (field.id === 'country') {
												return <Select variantClasses={['select__checkout']} key={field.id} id={`address-${field.id}`} options={[{ key: '', value: translate(setup, 'chooseCountry') }, ...Object.keys(setup.countries).sort((a, b) => setup.countries[a].name.localeCompare(setup.countries[b].name)).map((c) => ({ key: c, value: setup.countries[c].name }))]} selected={checkout?.payment?.address?.[field.id]} inputChange={onCountryChange} isError={formErrors[field.id]} />;
											}
											return <Input variantClasses={['label__checkout']} key={field.id} id={`address-${field.id}`} type={(field.id === 'phone' ? 'tel' : field.id)} value={checkout?.payment?.address?.[field.id]} inputChange={onAddressInputChange} inputBlur={onAddressInputChange} label={translate(setup, `label${upperCaseFirst(field.id)}`)} isError={formErrors[field.id]} />;
										})
									}
								</div>
								<div className={s.selection_info_finalize}>
									<div className={s.selection_info_finalize_checkbox}>
										<Checkbox isError={formErrors.termsAndConditions} id="termsAndConditions" inputChange={termsCheckedHandler} selected={checkout?.payment?.address?.termsAndConditions}><span dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutAcceptTerms'), { ALLOWED_TAGS: ['a'] }) }} /></Checkbox>
									</div>
									{ errorMessage && <span className={s.selection_info_finalize_errors}>{ errorMessage }</span> }
									<div className={[s.selection_info_finalize_button, s.selection_info_finalize_button__minor].join(' ')}>
										<Button variantClasses={['button__checkout', 'button__transparent', (buttonError ? 'button__error' : null)]} type="link" location={prefix(setup, 'shop')}>{ translate(setup, 'checkoutBackToShop') }</Button>
									</div>
									<div className={s.selection_info_finalize_button}>
										<Button variantClasses={['button__checkout', (buttonError ? 'button__error' : null)]} type="submit" disabled={buttonError} clickEventHandler={checkoutSubmitHandler}>{ translate(setup, 'goToCheckout') }</Button>
									</div>
									<div className={s.selection_info_finalize_shippingTaxes}>
										{ translate(setup, 'shippingTaxes') }
									</div>
								</div>
							</form>
						</div>
						<div className={s.selection_cart}>
							<ErrorBoundary message="Product data missing" debugData={checkout.selection}>
								<Cart position="selection" />
							</ErrorBoundary>
						</div>
					</div>
				) : (
					<div className={s.selection}>
						<h1 className={s.selection_title}>{ translate(setup, 'checkoutNoItems') }</h1>
					</div>
				)
			}
		</main>
	);
};


const mapState = ({ checkout, environment }) => {
	const { setup, header } = environment;
	return { checkout, setup, header };
};

export default hot(module)(connect(mapState, { updateCheckoutAddress, changeAddressInput, checkAddressInputErrors, changeCountry })(Selection));
